import * as ToastPrimitive from '@radix-ui/react-toast';
import { Check, ChevronRight, CircleAlert, Info, X } from 'lucide-react';
import { useState } from 'react';
import { cn } from '@/lib/cn';
import { hueClass } from '@/lib/hue';
import { CopyButton } from './CopyId';
import { dismissToast, useToasts, type ToastItem } from './toast-store';

function ToastView({ item }: { item: ToastItem }) {
  const [showDetails, setShowDetails] = useState(false);
  const Icon = item.tone === 'error' ? CircleAlert : item.tone === 'success' ? Check : Info;

  return (
    <ToastPrimitive.Root
      open={item.open}
      onOpenChange={(open) => {
        if (!open) dismissToast(item.id);
      }}
      duration={item.duration ?? 5000}
      className={cn(
        'group pointer-events-auto relative flex w-full gap-2.5 overflow-hidden rounded-lg border border-border-strong py-2.5 pr-9 pl-3 text-fg shadow-float glass-notice',
        'animate-toast-in data-[state=closed]:animate-toast-out',
        'data-[swipe=cancel]:translate-x-0 data-[swipe=end]:animate-toast-out data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)]',
        hueClass(item.tone === 'success' ? 'ok' : item.tone === 'error' ? 'danger' : 'ink'),
      )}
    >
      <span className="mt-0.5 flex size-4 shrink-0 items-center justify-center text-[var(--h)]">
        <Icon aria-hidden className="size-4" strokeWidth={item.tone === 'success' ? 2.5 : 1.75} />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <ToastPrimitive.Title className="text-base font-medium">{item.title}</ToastPrimitive.Title>
        {item.description && (
          <ToastPrimitive.Description className="text-sm text-fg-muted">
            {item.description}
          </ToastPrimitive.Description>
        )}
        {(item.requestId ?? item.code) && (
          <div className="mt-1">
            <button
              type="button"
              onClick={() => setShowDetails((v) => !v)}
              aria-expanded={showDetails}
              className="inline-flex items-center gap-0.5 text-xs text-fg-subtle hover:text-fg"
            >
              <ChevronRight
                aria-hidden
                className={cn('size-3 transition-transform', showDetails && 'rotate-90')}
              />
              Details
            </button>
            {showDetails && (
              <dl className="mt-1 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 rounded-md bg-subtle p-2 text-xs">
                {item.code && (
                  <>
                    <dt className="text-fg-subtle">Code</dt>
                    <dd className="truncate mono">{item.code}</dd>
                  </>
                )}
                {item.requestId && (
                  <>
                    <dt className="text-fg-subtle">Request</dt>
                    <dd className="flex min-w-0 items-center gap-1">
                      <span className="truncate mono">{item.requestId}</span>
                      <CopyButton value={item.requestId} label="Copy request id" />
                    </dd>
                  </>
                )}
              </dl>
            )}
          </div>
        )}
        {item.action && (
          <ToastPrimitive.Action altText={item.action.label} asChild>
            <button
              type="button"
              onClick={item.action.onClick}
              className="mt-1 self-start text-sm font-medium underline underline-offset-2"
            >
              {item.action.label}
            </button>
          </ToastPrimitive.Action>
        )}
      </div>
      <ToastPrimitive.Close
        aria-label="Dismiss"
        className="absolute top-2 right-2 grid size-5 place-items-center rounded-sm text-fg-subtle hover:bg-hover hover:text-fg"
      >
        <X aria-hidden className="size-2.5" strokeWidth={2.5} />
      </ToastPrimitive.Close>
    </ToastPrimitive.Root>
  );
}

export function Toaster() {
  const items = useToasts();
  return (
    <ToastPrimitive.Provider swipeDirection="right">
      {items.map((item) => (
        <ToastView key={item.id} item={item} />
      ))}
      <ToastPrimitive.Viewport className="fixed right-4 bottom-4 z-[100] flex w-[380px] max-w-[calc(100vw-2rem)] flex-col gap-2 outline-none" />
    </ToastPrimitive.Provider>
  );
}
